// Navbar with three style variants: 'pill' (default), 'bar', 'sidebar'.
// Includes profile menu, notifications popover and mobile hamburger.
import React from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { I } from '../../icons';
import Avatar from '../atoms/Avatar';
import IconButton from '../atoms/IconButton';
import { LinkButton } from '../atoms/Button';
import './Navbar.css';

const NAV_LINKS_PUBLIC = [
  { id: 'home', label: 'Home', to: '/', scrollTo: 'landing' },
  { id: 'about', label: 'About', to: '/', scrollTo: 'about' },
  { id: 'features', label: 'Features', to: '/', scrollTo: 'features' },
];

const NAV_LINKS_APP = [
  { id: 'dashboard', label: 'Dashboard', icon: 'Home', to: '/dashboard' },
  { id: 'classroom', label: 'Classroom', icon: 'Video', to: '/classroom' },
  { id: 'leaderboard', label: 'Leaderboard', icon: 'Trophy', to: '/leaderboard' },
  { id: 'coursework', label: 'Coursework', icon: 'Book', to: '/coursework' },
  { id: 'grades', label: 'Report', icon: 'Chart', to: '/grades' },
];

const NAV_LINKS_ADMIN = [
  { id: 'admin', label: 'Overview', icon: 'Chart', to: '/admin' },
  { id: 'admin-classroom', label: 'Classroom', icon: 'Video', to: '/admin/classroom' },
  { id: 'admin-submissions', label: 'Submissions', icon: 'Send', to: '/admin/submissions' },
  { id: 'admin-students', label: 'Students', icon: 'User', to: '/admin/students' },
  { id: 'admin-pending', label: 'Requests', icon: 'Bell', to: '/admin/pending' },
];

/**
 * Requests is hidden from the super admin.
 *
 * That queue is empty BY CONSTRUCTION for this account: pendingRegistrations
 * matches `{ verified: false, group: req.admin.group }`, and the super admin's
 * group is the literal string 'all', which no student's group equals. Approving
 * is blocked the same way — verifyStudent sits behind `studentFound`, which
 * requires the student's group to match. Student approvals genuinely belong to
 * each group's assistant; what this account approves is ASSISTANTS, under its own
 * menu below.
 */
const adminLinksFor = (isDok) =>
  (isDok ? NAV_LINKS_ADMIN.filter((l) => l.id !== 'admin-pending') : NAV_LINKS_ADMIN);

/**
 * The super admin's extra pages.
 *
 * They sit in their OWN menu rather than being appended to NAV_LINKS_ADMIN, for
 * two reasons: nine top-level links do not fit the pill on a laptop, and the
 * whole point of the brief is that the DOK sees the same admin view — so the
 * admin links must stay exactly as they are, with the super-admin powers added
 * beside them rather than mixed in.
 */
const NAV_LINKS_DOK = [
  { id: 'dok-assistants', label: 'Assistants', icon: 'Users', to: '/admin/assistants', hint: 'Approve and manage TAs' },
  { id: 'dok-groups', label: 'Groups', icon: 'Layers', to: '/admin/groups', hint: 'Create, rename, delete groups' },
  { id: 'dok-roster', label: 'All students', icon: 'User', to: '/admin/roster', hint: 'Every group, with their TA' },
  { id: 'dok-system', label: 'Maintenance', icon: 'Refresh', to: '/admin/system', hint: 'End-of-term resets' },
];

export const Logo = ({ small }) => (
  <Link to="/" className="logo" aria-label="Dr. Omar Khalid home">
    <span className="logo-mark">
      <I.Atom size={small ? 18 : 22} strokeWidth={2} />
    </span>
    {!small && (
      <span className="logo-text">
        <span className="logo-1">Dr. Omar</span>
        <span className="logo-2">Khalid</span>
      </span>
    )}
  </Link>
);

function NavItem({ link, className, onClick, children }) {
  return (
    <Link
      to={link.to}
      state={link.scrollTo ? { scrollTo: link.scrollTo } : undefined}
      className={className}
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

function NotificationsBell({ items, onMark }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const unread = items.filter((n) => !n.read).length;

  React.useEffect(() => {
    const onClick = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div className="nb-pop-wrap" ref={ref}>
      <IconButton className="nb-bell" onClick={() => setOpen((o) => !o)} aria-label="Notifications">
        <I.Bell size={18} />
        {unread > 0 && <span className="nb-dot">{unread}</span>}
      </IconButton>
      {open && (
        <div className="nb-pop card anim-pop">
          <div className="nb-pop-hd">
            <strong>Notifications</strong>
            <button className="nb-link" onClick={onMark}>Mark all read</button>
          </div>
          <div className="nb-list">
            {items.map((n) => (
              <div key={n.id} className={`nb-item ${n.read ? 'read' : ''}`}>
                <span className={`nb-tag chip-${n.kind || 'primary'}`}>{n.tag}</span>
                <div className="nb-body">
                  <div className="nb-title">{n.title}</div>
                  <div className="nb-meta muted">{n.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The DOK-only dropdown of super-admin pages.
 *
 * Rendered as part of the link row, so it reads as navigation rather than as an
 * account action — these are pages, not settings. `pathname` decides whether the
 * trigger shows as active, because none of its children are the current route
 * while the menu is closed and the user still needs to see where they are.
 */
function SuperAdminMenu({ pathname, onNavigate }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const active = NAV_LINKS_DOK.some((l) => pathname === l.to);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Closing on navigation: without this the menu stays open over the page it
  // just opened, because the click never leaves the component.
  const go = () => {
    setOpen(false);
    if (onNavigate) onNavigate();
  };

  return (
    <div className="nb-pop-wrap nb-sa-wrap" ref={ref}>
      <button
        className={`nb-sa-trigger ${active ? 'active' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <I.Shield size={16} />
        <span>Super admin</span>
        <I.ChevronDown size={13} />
      </button>
      {open && (
        <div className="nb-pop card anim-pop nb-sa-pop" role="menu">
          <div className="nb-pop-hd">
            <strong>Super admin</strong>
            <span className="muted fs-11">Owner only</span>
          </div>
          <div className="nb-pmenu">
            {NAV_LINKS_DOK.map((l) => (
              <Link key={l.id} to={l.to} role="menuitem" onClick={go} className={pathname === l.to ? 'on' : ''}>
                {I[l.icon]({ size: 16 })}
                <span className="grow">
                  {l.label}
                  <span className="nb-sa-hint">{l.hint}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileMenu({ user, onLogout }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onClick = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);
  if (!user) return null;
  const isAdmin = user.role === 'admin';
  // The DOK is the admin whose id is 1 — the same test the API's own
  // dok_middleware makes. See AuthContext's `isDok`.
  const isDok = isAdmin && String(user.id) === '1';
  return (
    <div className="nb-pop-wrap" ref={ref}>
      <button className="nb-profile" onClick={() => setOpen((o) => !o)} aria-label="Account">
        <Avatar name={user.name} size="sm" />
        <span className="nb-name">{user.name.split(' ')[0]}</span>
        <I.ChevronDown size={14} />
      </button>
      {open && (
        <div className="nb-pop card anim-pop nb-pop-profile">
          <div className="nb-pop-hd col align-start gap-4">
            <strong>{user.name}</strong>
            <span className="muted fs-12">
              {isDok ? 'Super admin · Owner' : isAdmin ? 'Assistant · Dr. Omar Khalid' : `Student · ID ${user.id}`}
            </span>
          </div>
          <div className="nb-pmenu">
            {/* Role-correct destinations. These used to point at /dashboard and
                /grades for everyone, which are student-only endpoints — an admin
                following them was bounced straight back to /admin by App's role
                redirect, so two of the three items did nothing. */}
            {isAdmin ? (
              <>
                <Link to="/admin" onClick={() => setOpen(false)}><I.Chart size={16} /> Class overview</Link>
                <Link to="/admin/students" onClick={() => setOpen(false)}><I.User size={16} /> My students</Link>
                {isDok && (
                  <Link to="/admin/assistants" onClick={() => setOpen(false)}>
                    <I.Shield size={16} /> Super admin
                  </Link>
                )}
              </>
            ) : (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)}><I.User size={16} /> Profile</Link>
                <Link to="/grades" onClick={() => setOpen(false)}><I.Chart size={16} /> My report</Link>
              </>
            )}
            <button className="danger" onClick={onLogout}>
              <I.Logout size={16} /> Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Bell + profile when signed in, log in / register when not. Shared by the bar
// and pill variants.
function NavActions({ user, notifications, onMarkRead, onLogout }) {
  if (user) {
    return (
      <>
        <NotificationsBell items={notifications} onMark={onMarkRead} />
        <ProfileMenu user={user} onLogout={onLogout} />
      </>
    );
  }
  return (
    <>
      <LinkButton to="/login" variant="ghost" size="sm">Log in</LinkButton>
      <LinkButton to="/register" variant="primary" size="sm">Register Now</LinkButton>
    </>
  );
}

export default function Navbar({ variant = 'pill', user, notifications, onMarkRead, onLogout }) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { pathname } = useLocation();
  const isApp = !!user;
  const isAdmin = user?.role === 'admin';
  // Same test as AuthContext's `isDok` and as the API's dok_middleware: the
  // super admin is admin id 1, not a role. Read from the user prop rather than
  // the context so the navbar stays renderable on its own in tests.
  const isDok = isAdmin && String(user.id) === '1';

  const links = isAdmin ? adminLinksFor(isDok) : isApp ? NAV_LINKS_APP : NAV_LINKS_PUBLIC;

  // Scrollspy for the public one-page landing: the active link follows the
  // section currently in view rather than the (always '/') route.
  const [activeSection, setActiveSection] = React.useState('home');
  React.useEffect(() => {
    if (isApp || pathname !== '/') return;
    const onScroll = () => {
      const probe = 130; // px below the fixed navbar
      let current = 'home';
      for (const id of ['about', 'features']) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= probe) current = id;
      }
      setActiveSection((prev) => (prev === current ? prev : current));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isApp, pathname]);

  const isActive = (l) => {
    if (!isApp) return l.id === activeSection;
    if (l.scrollTo) return false;
    if (l.to === '/' || l.to === '/admin') return pathname === l.to;
    return pathname.startsWith(l.to);
  };

  // SIDEBAR variant — only render when logged in
  if (variant === 'sidebar' && isApp) {
    return (
      <aside className="nb-sidebar">
        <div className="nb-sb-top"><Logo /></div>
        <nav className="nb-sb-links">
          {links.map((l) => (
            <NavItem key={l.id} link={l} className={isActive(l) ? 'active' : ''}>
              {l.icon && I[l.icon]({ size: 18 })}
              <span>{l.label}</span>
            </NavItem>
          ))}
          {/* A sidebar has the vertical room the pill does not, so the
              super-admin pages get a labelled section here instead of a
              dropdown. */}
          {isDok && (
            <>
              <span className="nb-sb-head"><I.Shield size={13} /> Super admin</span>
              {NAV_LINKS_DOK.map((l) => (
                <NavItem key={l.id} link={l} className={pathname === l.to ? 'active' : ''}>
                  {I[l.icon]({ size: 18 })}
                  <span>{l.label}</span>
                </NavItem>
              ))}
            </>
          )}
        </nav>
        <div className="nb-sb-bottom">
          <NotificationsBell items={notifications} onMark={onMarkRead} />
          <ProfileMenu user={user} onLogout={onLogout} />
        </div>
      </aside>
    );
  }

  // BAR variant — flat full-width clean bar
  if (variant === 'bar') {
    return (
      <header className="nb-bar">
        <div className="nb-bar-inner">
          <Logo />
          <nav className="nb-links">
            {links.map((l) => (
              <NavItem key={l.id} link={l} className={isActive(l) ? 'active' : ''}>{l.label}</NavItem>
            ))}
            {isDok && <SuperAdminMenu pathname={pathname} />}
          </nav>
          <div className="nb-actions">
            <NavActions
              user={user}
              notifications={notifications}
              onMarkRead={onMarkRead}
              onLogout={onLogout}
            />
          </div>
        </div>
      </header>
    );
  }

  // PILL variant (default)
  return (
    <header className={`nb-pill-wrap ${mobileOpen ? 'mobile-open' : ''}`}>
      <div className="nb-pill">
        <Logo />
        <nav className="nb-links">
          {links.map((l) => (
            <NavItem key={l.id} link={l} className={isActive(l) ? 'active' : ''}>
              {l.icon && I[l.icon]({ size: 16 })}
              <span>{l.label}</span>
            </NavItem>
          ))}
          {isDok && <SuperAdminMenu pathname={pathname} />}
        </nav>
        <div className="nb-actions">
          <NavActions
            user={user}
            notifications={notifications}
            onMarkRead={onMarkRead}
            onLogout={onLogout}
          />
          <button className="nb-burger" onClick={() => setMobileOpen((o) => !o)} aria-label="Menu">
            {mobileOpen ? <I.Close size={20} /> : <I.Menu size={20} />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="nb-mobile card">
          {links.map((l) => (
            <NavItem key={l.id} link={l} className={isActive(l) ? 'active' : ''} onClick={() => setMobileOpen(false)}>
              {l.icon && I[l.icon]({ size: 18 })}<span>{l.label}</span>
            </NavItem>
          ))}
          {/* Below 1024px the link row is hidden and this menu IS the
              navigation, so the super-admin pages have to appear here too — a
              dropdown inside a dropdown would be unusable on a phone, so they
              are a labelled section instead. */}
          {isDok && (
            <>
              <hr />
              <span className="nb-mobile-head"><I.Shield size={14} /> Super admin</span>
              {NAV_LINKS_DOK.map((l) => (
                <NavItem
                  key={l.id}
                  link={l}
                  className={pathname === l.to ? 'active' : ''}
                  onClick={() => setMobileOpen(false)}
                >
                  {I[l.icon]({ size: 18 })}<span>{l.label}</span>
                </NavItem>
              ))}
            </>
          )}
          <hr />
          {isApp ? (
            <button onClick={onLogout}><I.Logout size={18} /> Log out</button>
          ) : (
            <>
              <Link to="/login" onClick={() => setMobileOpen(false)}>Log in</Link>
              <Link to="/register" className="primary" onClick={() => setMobileOpen(false)}>Register Now</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}

Navbar.propTypes = {
  variant: PropTypes.oneOf(['pill', 'bar', 'sidebar']),
  user: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.string,
    role: PropTypes.oneOf(['student', 'admin']),
  }),
  notifications: PropTypes.array,
  onMarkRead: PropTypes.func,
  onLogout: PropTypes.func,
};
